import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Save, Share2, Crown, Sparkles, CheckCircle2, XCircle, Wallet } from "lucide-react";

const money = (n) => `€${(Number(n) || 0).toFixed(2)}`;
const statusBadge = (s) => {
  const cls = { active: "bg-green-600", pending: "bg-amber-500", rejected: "bg-red-500", suspended: "bg-slate-500" }[s] || "bg-slate-400";
  return <Badge className={`${cls} capitalize`}>{s}</Badge>;
};

export const PartnerManager = () => {
  const [settings, setSettings] = useState(null);
  const [rows, setRows] = useState(null);
  const [wds, setWds] = useState([]);
  const [busy, setBusy] = useState(null);
  const [savingS, setSavingS] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/partner-settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/partners").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/admin/partner-withdrawals").then((r) => setWds(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSavingS(true);
    try { await api.put("/admin/partner-settings", settings); toast.success("Rates saved"); }
    catch { toast.error("Failed"); }
    setSavingS(false);
  };

  const act = async (fn, id, okMsg) => {
    setBusy(id);
    try { await fn(); toast.success(okMsg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(null);
  };

  if (!settings || !rows) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const field = (k, label) => (
    <div>
      <label className="text-xs text-muted-foreground block mb-1">{label}</label>
      <Input type="number" step="0.1" className="h-9 w-28" data-testid={`ps-${k}`} value={settings[k]} onChange={(e) => setSettings((s) => ({ ...s, [k]: e.target.value }))} />
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Global rates */}
      <Card className="p-5">
        <h3 className="font-semibold text-sm mb-3">Program rates (platform-funded)</h3>
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <p className="flex items-center gap-1.5 text-sm font-medium mb-2"><Sparkles className="w-4 h-4 text-primary" />Affiliate</p>
            <div className="flex gap-3">{field("affiliate_sales_pct", "Sales %")}{field("affiliate_signup_reward", "Sign-up € reward")}</div>
          </div>
          <div>
            <p className="flex items-center gap-1.5 text-sm font-medium mb-2"><Crown className="w-4 h-4 text-primary" />Influencer</p>
            <div className="flex gap-3">{field("influencer_sales_pct", "Sales %")}{field("influencer_signup_reward", "Sign-up € reward")}</div>
          </div>
        </div>
        <Button className="mt-4 h-9" data-testid="save-partner-settings" disabled={savingS} onClick={saveSettings}>
          {savingS ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Save rates</>}
        </Button>
      </Card>

      {/* Partners */}
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b bg-muted/30 flex items-center gap-2"><Share2 className="w-4 h-4 text-primary" /><h3 className="font-semibold text-sm">Partners</h3></div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Partner</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead>
              <TableHead>Sign-ups</TableHead><TableHead>Sales</TableHead><TableHead>Earned</TableHead>
              <TableHead>Override %</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground text-sm">No partners yet.</TableCell></TableRow>}
              {rows.map((r) => (
                <TableRow key={r.partner_id} data-testid={`partner-row-${r.partner_id}`}>
                  <TableCell><p className="font-medium text-sm">{r.name || "—"}</p><p className="text-[11px] text-muted-foreground">{r.email}</p></TableCell>
                  <TableCell className="capitalize">{r.type === "influencer" ? <Crown className="w-3.5 h-3.5 inline mr-1 text-primary" /> : <Sparkles className="w-3.5 h-3.5 inline mr-1 text-primary" />}{r.type}</TableCell>
                  <TableCell>{statusBadge(r.status)}</TableCell>
                  <TableCell>{r.signups}</TableCell>
                  <TableCell>{r.sales_count} ({money(r.sales_amount)})</TableCell>
                  <TableCell className="text-green-600 font-medium">{money(r.earnings)}</TableCell>
                  <TableCell>
                    <Input type="number" step="0.1" className="h-8 w-20" placeholder="—" defaultValue={r.sales_pct_override ?? ""} data-testid={`override-${r.partner_id}`}
                      onBlur={(e) => { const v = e.target.value; if (v !== "" && parseFloat(v) !== r.sales_pct_override) act(() => api.post(`/admin/partners/${r.partner_id}`, { sales_pct_override: parseFloat(v) }), r.partner_id, "Override set"); }} />
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap space-x-1">
                    {r.status === "pending" && r.type === "influencer" && (
                      <>
                        <Button size="sm" className="h-8" data-testid={`approve-${r.partner_id}`} disabled={busy === r.partner_id} onClick={() => act(() => api.post(`/admin/partners/${r.partner_id}/approve`), r.partner_id, "Approved")}><CheckCircle2 className="w-4 h-4 mr-1" />Approve</Button>
                        <Button size="sm" variant="outline" className="h-8" data-testid={`reject-${r.partner_id}`} disabled={busy === r.partner_id} onClick={() => act(() => api.post(`/admin/partners/${r.partner_id}/reject`), r.partner_id, "Rejected")}>Reject</Button>
                      </>
                    )}
                    {r.status === "active" && (
                      <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200" data-testid={`suspend-${r.partner_id}`} disabled={busy === r.partner_id} onClick={() => act(() => api.post(`/admin/partners/${r.partner_id}`, { status: "suspended" }), r.partner_id, "Suspended")}><XCircle className="w-4 h-4 mr-1" />Suspend</Button>
                    )}
                    {r.status === "suspended" && (
                      <Button size="sm" className="h-8" data-testid={`reactivate-${r.partner_id}`} disabled={busy === r.partner_id} onClick={() => act(() => api.post(`/admin/partners/${r.partner_id}`, { status: "active" }), r.partner_id, "Reactivated")}>Reactivate</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Payout requests */}
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b bg-muted/30 flex items-center gap-2"><Wallet className="w-4 h-4 text-primary" /><h3 className="font-semibold text-sm">Payout requests</h3></div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Partner</TableHead><TableHead>Amount</TableHead><TableHead>Details</TableHead><TableHead>Status</TableHead><TableHead>Requested</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
            <TableBody>
              {wds.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground text-sm">No payout requests.</TableCell></TableRow>}
              {wds.map((w) => (
                <TableRow key={w.wd_id} data-testid={`pwd-row-${w.wd_id}`}>
                  <TableCell><p className="text-sm font-medium">{w.name}</p><p className="text-[11px] text-muted-foreground">{w.email}</p></TableCell>
                  <TableCell className="font-bold">{money(w.amount)}</TableCell>
                  <TableCell className="text-xs max-w-[220px] truncate">{w.details || "—"}</TableCell>
                  <TableCell>{w.status === "paid" ? <Badge className="bg-green-600">Paid</Badge> : <Badge variant="outline">Requested</Badge>}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(w.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    {w.status !== "paid" && <Button size="sm" className="h-8" data-testid={`pay-${w.wd_id}`} disabled={busy === w.wd_id} onClick={() => act(() => api.post(`/admin/partner-withdrawals/${w.wd_id}/pay`), w.wd_id, "Marked paid")}>Mark paid</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};
